/**
 * Local Network Access (LNA) utility functions.
 *
 * Chrome 142+ requires user permission for web pages on public domains
 * to make requests to local network addresses (like 127.0.0.1).
 *
 * @see https://developer.chrome.com/blog/local-network-access
 */

export type LocalNetworkAccessResult = {
  granted: boolean;
  permissionState: PermissionState | null;
  error?: Error;
};

/**
 * Checks LNA permission status using the Permissions API.
 * Returns 'granted', 'prompt', or 'denied'.
 */
async function queryLnaPermission(): Promise<PermissionState | null> {
  try {
    // @ts-expect-error - 'local-network-access' is not in TypeScript's PermissionName type yet
    const result = await navigator.permissions.query({ name: 'local-network-access' });
    return result.state;
  } catch {
    // Browser doesn't support querying LNA permission
    return null;
  }
}

/**
 * Requests Local Network Access permission by checking the permission status
 * and making a fetch to the local server to trigger the prompt if needed.
 *
 * @param port - The port number of the local server (e.g., 4269)
 * @returns Promise resolving to whether permission was granted
 */
export async function requestLocalNetworkAccess(port: number): Promise<LocalNetworkAccessResult> {
  // First, check permission status via Permissions API
  const permissionState = await queryLnaPermission();

  // If already granted, we can proceed
  if (permissionState === 'granted') {
    return { granted: true, permissionState };
  }

  // Always try the fetch - this may trigger the permission prompt
  // Even if previously denied, the user might have reset permissions in settings
  try {
    const response = await fetch(`http://localhost:${port}/healthz`, {
      method: 'GET',
    });

    if (response.ok) {
      return { granted: true, permissionState };
    }

    // Server responded but with an error status - permission was still granted
    return {
      granted: true,
      permissionState,
      error: new Error(`Server responded with status ${response.status}`),
    };
  } catch (error) {
    // Re-check permission state after failed fetch
    const currentPermissionState = await queryLnaPermission();
    
    // If permission is now granted (user just allowed it), consider it a success
    // The fetch might have failed due to timing, but permission is granted
    if (currentPermissionState === 'granted') {
      return {
        granted: true,
        permissionState: currentPermissionState,
      };
    }
    
    // This could be:
    // 1. User denied LNA permission
    // 2. Server is not running
    // 3. Network error
    return {
      granted: false,
      permissionState: currentPermissionState,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
}

/**
 * Checks if the current browser supports Local Network Access permission prompts.
 * This is a Chrome 138+ feature.
 */
export function supportsLocalNetworkAccess(): boolean {
  // Check if we're in a secure context (required for LNA)
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return false;
  }

  // LNA is a Chromium feature, check for Chrome
  const isChromium = typeof navigator !== 'undefined' && /Chrome/.test(navigator.userAgent);

  return isChromium;
}
